<script setup>
const { $isMobile } = useNuxtApp();
const route = useRoute();
const goodsId = route.params.id;

import { getGoodsInfo, getGoodsComments } from "~/api/api";
import { addGoodsBrowse, checkPay } from "~/api/user";
import { useOrderPolling } from "~/composables/useOrderPolling";

const goodsInfoData = ref(null);
const loading = ref(true);

// 获取商品信息
const fetchGoodsInfo = async () => {
	try {
		loading.value = true;
		const res = await getGoodsInfo({ goods_id: goodsId });
		goodsInfoData.value = res.data;
	} catch (error) {
		console.error("获取商品信息失败:", error);
	} finally {
		loading.value = false;
	}
};

useOrderPolling();

onMounted(async () => {
	await fetchGoodsInfo();

	// 检查用户是否有token，有token时才调用addGoodsBrowse
	const token = localStorage.getItem("token");
	if (token) {
		addGoodsBrowse({ goods_id: goodsId });
	}
});

// 设置页面元数据
definePageMeta({
	"m-showNavBar": true,
	"m-showFooter": true,
	"pc-showNavBar": true,
	"pc-showFooter": true,
	breadcrumbName: "開運吉品",
});

// 响应式设置SEO头部信息
useHead({
	title: computed(() => goodsInfoData.value?.title || "開運吉品 - 先知命局"),
	meta: [
		{
			name: "keywords",
			content: computed(
				() =>
					`${goodsInfoData.value?.title || "開運吉品"}, 開運風水吉祥物, 招財擺件, 風水飾品, 鎮宅符咒, 天然能量水晶, 轉運小物, 先知命局`
			),
		},
		{
			name: "description",
			content: computed(() =>
				goodsInfoData.value?.title
					? `${goodsInfoData.value.title} - 先知命局嚴選開運風水吉祥物，大師開光加持，助您改善財運、事業、桃花。限時優惠，立即購買能量滿滿的風水小物！`
					: "先知命局嚴選開運風水吉祥物，大師開光加持，助您改善財運、事業、桃花。限時優惠，立即購買能量滿滿的風水小物！"
			),
		},
		{
			property: "og:title",
			content: computed(() => goodsInfoData.value?.title || "開運吉品 - 先知命局"),
		},
		{
			property: "og:description",
			content: computed(() =>
				goodsInfoData.value?.title
					? `${goodsInfoData.value.title} - 先知命局嚴選開運風水吉祥物`
					: "先知命局嚴選開運風水吉祥物，大師開光加持"
			),
		},
		{
			property: "og:image",
			content: computed(
				() => goodsInfoData.value?.detail_images?.[0] || goodsInfoData.value?.cover || "/logo.png"
			),
		},
		{
			property: "og:type",
			content: "product",
		},
	],
});

// 动态导入组件（代码分割）
const DynamicComponent = $isMobile
	? defineAsyncComponent(() => import("@variants/seer/goods-detail/m/index.vue"))
	: defineAsyncComponent(() => import("@variants/seer/goods-detail/pc/index.vue"));
</script>

<template>
	<main>
		<div v-if="loading" class="loading-container">
			<div class="loading-spinner"></div>
		</div>
		<DynamicComponent v-else-if="goodsInfoData" :goods-info-data="goodsInfoData" />
	</main>
</template>

<style scoped>
.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	padding: 40px;
}

.loading-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #f05222;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 16px;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.loading-container p {
	font-family:
		PingFang SC,
		PingFang SC;
	font-size: 16px;
	color: #666;
	margin: 0;
}
</style>
